/**
 * ProctoringStatusPanel.jsx
 * Minimized/expandable floating panel for live proctoring status indicators.
 */

import React, { useEffect, useRef, useState } from 'react';

function statusColor({ integrityScore, violations }) {
  if (integrityScore < 50 || violations >= 8) return 'bg-red-500';
  if (integrityScore < 80 || violations >= 4) return 'bg-amber-500';
  return 'bg-emerald-500';
}

function faceLabel(status) {
  if (status === 'detected') return 'Face: Detected';
  if (status === 'absent') return 'Face: Not visible';
  if (status === 'multiple') return 'Face: Multiple faces';
  return 'Face: Unknown';
}

function gazeLabel(status) {
  if (status === 'on_screen') return 'Gaze: On screen';
  if (status === 'away') return 'Gaze: Away';
  if (status === 'down') return 'Gaze: Looking down';
  return 'Gaze: Unknown';
}

export default function ProctoringStatusPanel({
  faceStatus,
  gazeStatus,
  violations,
  integrityScore,
  isFullscreen,
  onRequestFullscreen,
  videoRef
}) {
  const [expanded, setExpanded] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    if (!expanded || !previewRef.current) return;
    // ADDED: mirror active webcam stream in panel preview when available.
    const source = videoRef?.current?.srcObject;
    if (source) {
      previewRef.current.srcObject = source;
    }
  }, [expanded, videoRef]);

  const badgeColor = statusColor({ integrityScore, violations });
  const scoreColor = integrityScore >= 80 ? 'text-emerald-600' : integrityScore >= 50 ? 'text-amber-600' : 'text-red-600';

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className={`fixed bottom-5 right-5 z-[1100] h-11 w-11 rounded-full shadow-lg ${badgeColor}`}
        aria-label="Open proctoring status"
      />
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-[1100] w-72 rounded-xl border border-slate-200 bg-white shadow-xl">
      <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
        <p className="text-sm font-semibold text-slate-800">Proctoring Status</p>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-xs text-slate-500 hover:text-slate-700"
        >
          Minimize
        </button>
      </div>

      <div className="p-3 space-y-2">
        <div className="h-28 rounded-lg overflow-hidden bg-slate-900">
          <video ref={previewRef} autoPlay muted playsInline className="h-full w-full object-cover" />
        </div>
        <p className="text-xs text-slate-700">{faceLabel(faceStatus)}</p>
        <p className="text-xs text-slate-700">{gazeLabel(gazeStatus)}</p>
        <p className={`text-xs font-semibold ${scoreColor}`}>Integrity Score: {integrityScore}</p>
        <p className="text-xs text-slate-700">Violations: {violations}</p>
        {!isFullscreen ? (
          <button
            type="button"
            onClick={onRequestFullscreen}
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
          >
            Return to Fullscreen
          </button>
        ) : null}
      </div>
    </div>
  );
}
